import type { ICreateDocumentCmd } from "@/data/commands/document/CreateDocumentCmd";
import type { ICreateDocumentConditionCmd } from "@/data/commands/document/CreateDocumentConditionCmd";
import type { IDocument, IDocumentCondition, IDocumentSchedulePreview } from "@/data/model/Document";
import { Weekday } from "@/data/model/Enums";
import type { IDocumentConditionQry } from "@/data/queries/document/DocumentConditionQry";
import type { IDocumentSchedulePreviewQry } from "@/data/queries/document/DocumentSchedulePreviewQry";
import type { IDocumentsQry } from "@/data/queries/document/DocumentsQry";
import { DateTime } from "@/data/util/DateTime";
import { Misc } from "@/data/util/Misc";

import {
    BaseServiceDao,
    type IDataSource,
    type IEditableDataSource,
    type IPaginatedResult,
    parseCollection,
    parsePaginatedResult,
} from "./BaseServiceDao";

export class DocumentDao extends BaseServiceDao implements IEditableDataSource<IDocument> {
    public static className = "DocumentDao";

    constructor() {
        super("document");
        this.isLocationDependent = true;
    }

    public async getSingle(id: string): Promise<IDocument> {
        return this.get(id, undefined, DocumentDao.parseDocument);
    }

    public async getData(qry: IDocumentsQry): Promise<IPaginatedResult<IDocument>> {
        return this.get("", qry, (f) => parsePaginatedResult(f, DocumentDao.parseDocument));
    }

    public async getActiveDocuments(): Promise<Array<IDocument>> {
        return this.get("active", undefined, (f) => parseCollection(f, DocumentDao.parseDocument));
    }

    public async create(cmd: ICreateDocumentCmd): Promise<IDocument> {
        return this.post("", cmd, DocumentDao.parseDocument, true);
    }

    public async update(id: string, cmd: ICreateDocumentCmd): Promise<IDocument> {
        return this.put(id, cmd, DocumentDao.parseDocument, true);
    }

    public async remove(id: string): Promise<void> {
        return this.delete(id, {});
    }

    public async getConditions(
        documentId: string,
        qry: IDocumentConditionQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IDocumentCondition>> {
        return this.get(
            documentId + "/condition/",
            qry,
            (f) => parsePaginatedResult(f, DocumentDao.parseCondition),
            abortSignal
        );
    }

    public async createCondition(documentId: string, cmd: ICreateDocumentConditionCmd): Promise<IDocumentCondition> {
        cmd.weekdays = Misc.enumsToStringArray(Weekday, cmd.weekdays) as any;
        return this.post(documentId + "/condition/", cmd, DocumentDao.parseCondition, true);
    }

    public async updateCondition(
        documentId: string,
        conditionId: string,
        cmd: ICreateDocumentConditionCmd
    ): Promise<IDocumentCondition> {
        cmd.weekdays = Misc.enumsToStringArray(Weekday, cmd.weekdays) as any;
        return this.put(documentId + "/condition/" + conditionId, cmd, DocumentDao.parseCondition, true);
    }

    public async removeCondition(documentId: string, conditionId: string): Promise<void> {
        return this.delete(documentId + "/condition/" + conditionId, {});
    }

    public async getSchedulePreview(qry: IDocumentSchedulePreviewQry): Promise<Array<IDocumentSchedulePreview>> {
        return this.get("preview", qry, (f) => parseCollection(f, DocumentDao.parsePreview));
    }

    public getConditionDataSource(documentId: string): IDataSource<IDocumentCondition> {
        return {
            getData: (filter, abortSignal) => this.getConditions(documentId, filter, abortSignal),
        };
    }

    public static parseDocument(literal: any): IDocument {
        return {
            id: literal.id,
            locationId: literal.locationId,
            isPublished: literal.isPublished,
            name: literal.name,
            displayNameDe: literal.displayNameDe != null ? literal.displayNameDe : undefined,
            displayNameEn: literal.displayNameEn != null ? literal.displayNameEn : undefined,
            displayNameFr: literal.displayNameFr != null ? literal.displayNameFr : undefined,
            displayNameIt: literal.displayNameIt != null ? literal.displayNameIt : undefined,
            order: literal.order != null ? parseInt(literal.order) : undefined,
            fileDe: literal.fileDe != null ? literal.fileDe : undefined,
            fileEn: literal.fileEn != null ? literal.fileEn : undefined,
            fileFr: literal.fileFr != null ? literal.fileFr : undefined,
            fileIt: literal.fileIt != null ? literal.fileIt : undefined,
            modified: DateTime.parseIsoDate(literal.modified),
            created: DateTime.parseIsoDate(literal.created),
            type: parseInt(literal.type) as any,
        };
    }

    public static parseCondition(literal: any): IDocumentCondition {
        return {
            id: literal.id,
            dateFrom: literal.dateFrom != null ? DateTime.parseIsoDate(literal.dateFrom) : undefined,
            dateUntil: literal.dateUntil != null ? DateTime.parseIsoDate(literal.dateUntil) : undefined,
            timeFrom: literal.timeFrom != null ? literal.timeFrom : undefined,
            timeUntil: literal.timeUntil != null ? literal.timeUntil : undefined,
            weekdays:
                literal.weekdays == null ? [] : parseCollection<Weekday>(literal.weekdays, (c) => (Weekday as any)[c]),
            roomId: literal.roomId == null ? undefined : literal.roomId,
        };
    }

    public static parsePreview(literal: any): IDocumentSchedulePreview {
        return {
            id: literal.id,
            isAllDay: literal.isAllDay,
            downloadPath: literal.downloadPath,
            start: DateTime.parseIsoDate(literal.start),
            end: DateTime.parseIsoDate(literal.end),
            name: literal.name,
        };
    }
}
